// src/media-uri.ts
var DEFAULT_SCHEME = "nrk";
var URI_SEGMENT_MAP = [
  [
    // Example: p1
    ["id", void 0]
  ],
  [
    // Example: nrk:p1
    ["scheme", void 0],
    ["id", void 0]
  ],
  [
    // Example: nrk:podcast:l_20358447-91cc-440b-b584-4791cc940bde
    ["scheme", "nrk"],
    ["type", "podcast"],
    ["episode", void 0]
  ],
  [
    // Example: nrk:channel:p1
    ["scheme", void 0],
    ["type", void 0],
    ["id", void 0]
  ],
  [
    // Example: nrk:podcast:bjoernen_lyver:l_f106ffee-bbbb-407e-86ff-eebbbb907e57
    ["scheme", "nrk"],
    ["type", "podcast"],
    ["series", void 0],
    ["episode", void 0]
  ],
  [
    ["scheme", "nrk"],
    ["type", "radioclip"],
    ["clipType", void 0],
    ["id", void 0]
  ]
];
var MediaURI = class _MediaURI {
  scheme;
  type;
  series;
  clipType;
  episode;
  id;
  constructor(parts) {
    this.scheme = parts.scheme ?? DEFAULT_SCHEME;
    this.type = parts.type;
    this.series = parts.series;
    this.clipType = parts.clipType;
    this.episode = parts.episode;
    this.id = parts.id;
  }
  static parse(uri) {
    if (!_MediaURI.isValidFormat(uri)) {
      return void 0;
    }
    const segments = uri.split(":");
    const parsedSegments = URI_SEGMENT_MAP.filter((targetSegments) => targetSegments.length === segments.length).map(
      (targetSegments) => targetSegments.reduce(
        (result, targetSegment, index) => {
          if (result === void 0) {
            return result;
          }
          if (targetSegment[1] !== void 0 && targetSegment[1] !== segments[index]) {
            return void 0;
          }
          result[targetSegment[0]] = segments[index];
          return result;
        },
        {}
      )
    ).find((result) => result !== void 0);
    return parsedSegments ? new _MediaURI(parsedSegments) : void 0;
  }
  static isValidFormat(uri) {
    return /^[\w-]+(:[\wÆØÅæøå-]+){0,3}$/.exec(uri) !== null;
  }
  /**
   * Returns the URL segments as a URL path, ignoring the scheme.
   *
   * For example: nrk:channel:p1 becomes /channel/p1.
   */
  asPath() {
    return `/${[this.type, this.series, this.episode, this.clipType, this.id].filter((s) => s).join("/")}`;
  }
  toString() {
    return [this.scheme, this.type, this.series, this.episode, this.clipType, this.id].filter((s) => s).join(":");
  }
};

// src/psapi-client.ts
import { getLogger, toError as toError2 } from "@nrk/player-core/utils.js";

// src/error.ts
var PSAPIRequestErrorType = /* @__PURE__ */ ((PSAPIRequestErrorType2) => {
  PSAPIRequestErrorType2["BAD_RESPONSE_ERROR"] = "BAD_RESPONSE_ERROR";
  PSAPIRequestErrorType2["NETWORK_ERROR"] = "NETWORK_ERROR";
  PSAPIRequestErrorType2["NOT_FOUND_ERROR"] = "NOT_FOUND_ERROR";
  PSAPIRequestErrorType2["TIMEOUT_ERROR"] = "TIMEOUT_ERROR";
  return PSAPIRequestErrorType2;
})(PSAPIRequestErrorType || {});
var NonPlayableErrorType = /* @__PURE__ */ ((NonPlayableErrorType2) => {
  NonPlayableErrorType2["CHANNEL_AVAILABLE_NORWEGIAN_RESIDENT_CLAIM_ERROR"] = "CHANNEL_AVAILABLE_NORWEGIAN_RESIDENT_CLAIM_ERROR";
  NonPlayableErrorType2["CHANNEL_GEOBLOCKED_ERROR"] = "CHANNEL_GEOBLOCKED_ERROR";
  NonPlayableErrorType2["CHANNEL_UNAVAILABLE_ERROR"] = "CHANNEL_UNAVAILABLE_ERROR";
  NonPlayableErrorType2["CHANNEL_EXCEEDS_PROFILE_RESTRICTIONS"] = "CHANNEL_EXCEEDS_PROFILE_RESTRICTIONS";
  NonPlayableErrorType2["CHANNEL_EXCEEDS_PROFILE_AGE_LIMIT"] = "CHANNEL_EXCEEDS_PROFILE_AGE_LIMIT";
  NonPlayableErrorType2["PROGRAM_AVAILABLE_NORWEGIAN_RESIDENT_CLAIM_ERROR"] = "PROGRAM_AVAILABLE_NORWEGIAN_RESIDENT_CLAIM_ERROR";
  NonPlayableErrorType2["PROGRAM_GEOBLOCKED_ERROR"] = "PROGRAM_GEOBLOCKED_ERROR";
  NonPlayableErrorType2["PROGRAM_IN_LIVE_BUFFER_ERROR"] = "PROGRAM_IN_LIVE_BUFFER_ERROR";
  NonPlayableErrorType2["PROGRAM_NOT_TRANSCODED_ERROR"] = "PROGRAM_NOT_TRANSCODED_ERROR";
  NonPlayableErrorType2["PROGRAM_RIGHTS_EXPIRED_ERROR"] = "PROGRAM_RIGHTS_EXPIRED_ERROR";
  NonPlayableErrorType2["PROGRAM_RIGHTS_NOT_STARTED_ERROR"] = "PROGRAM_RIGHTS_NOT_STARTED_ERROR";
  NonPlayableErrorType2["PROGRAM_EXCEEDS_PROFILE_RESTRICTIONS"] = "PROGRAM_EXCEEDS_PROFILE_RESTRICTIONS";
  NonPlayableErrorType2["PROGRAM_EXCEEDS_PROFILE_AGE_LIMIT"] = "PROGRAM_EXCEEDS_PROFILE_AGE_LIMIT";
  NonPlayableErrorType2["PROGRAM_UNAVAILABLE_ERROR"] = "PROGRAM_UNAVAILABLE_ERROR";
  NonPlayableErrorType2["UNPLAYABLE_ERROR"] = "UNPLAYABLE_ERROR";
  return NonPlayableErrorType2;
})(NonPlayableErrorType || {});
var AbortError = class _AbortError extends Error {
  constructor(message) {
    super(message);
    Object.setPrototypeOf(this, _AbortError.prototype);
    this.name = "AbortError";
  }
};
var PSAPIRequestError = class _PSAPIRequestError extends Error {
  constructor(message, type) {
    super(message);
    this.type = type;
    Object.setPrototypeOf(this, _PSAPIRequestError.prototype);
    this.name = "PSAPIRequestError";
  }
};
var NonPlayableError = class _NonPlayableError extends Error {
  constructor(message, type, endUserMessage, endUserMessageSupplement) {
    super(message);
    this.type = type;
    Object.setPrototypeOf(this, _NonPlayableError.prototype);
    this.name = "NonPlayableError";
    this.endUserMessage = endUserMessage;
    this.endUserMessageSupplement = endUserMessageSupplement;
  }
  endUserMessage;
  endUserMessageSupplement;
};

// src/psapi-data.ts
import { parse as parseDuration, toSeconds } from "iso8601-duration";

// src/time-with-tz.ts
var ROTATION = 60;
var RADIX = 10;
var pad = (n) => n < 10 ? `0${n}` : n;
var TimeWithTZ = class _TimeWithTZ {
  /**
   * Milliseconds since epoch.
   */
  utc;
  /**
   * Time zone offset in minutes.
   */
  tzOffset;
  /**
   * @param utc Milliseconds since epoch.
   * @param tzOffset Time zone offset in minutes.
   */
  constructor(utc, tzOffset) {
    this.utc = utc;
    this.tzOffset = tzOffset;
  }
  /**
   * Parses the '/Date(<ms since epoch><time zone>)/' format and returns an
   * instance of the class, if successful.
   */
  static parse(timestamp) {
    const pattern = /^\/Date\((\d+)(?:([+-]\d{2})(\d{2}))?\)\/$/;
    const result = pattern.exec(timestamp);
    if (result) {
      const utc = parseInt(result[1], RADIX);
      const tzOffset = result.length > 2 ? parseInt(result[2], RADIX) * ROTATION + parseInt(result[3], RADIX) : 0;
      return new _TimeWithTZ(utc, tzOffset);
    }
    throw new Error(`Unknown format: ${timestamp}`);
  }
  /**
   * Returns an ISO 8601 timestamp looking like 'YYYY-mm-ddTHH:MM:SS+ZZ:ZZ'.
   * Milliseconds are ignored.
   */
  toModernPSAPIFormat() {
    const date = new Date(this.utc + this.tzOffset * ROTATION * 1e3);
    return [
      date.getUTCFullYear(),
      "-",
      pad(date.getUTCMonth() + 1),
      "-",
      pad(date.getUTCDate()),
      "T",
      pad(date.getUTCHours()),
      ":",
      pad(date.getUTCMinutes()),
      ":",
      pad(date.getUTCSeconds()),
      this.tzOffset < 0 ? "-" : "+",
      pad(Math.floor(this.tzOffset / ROTATION)),
      ":",
      pad(this.tzOffset % ROTATION)
    ].join("");
  }
  /**
   * Returns a compact date format (YYYMMDD).
   */
  toCompactLocalDate() {
    const date = new Date(this.utc + this.tzOffset * ROTATION * 1e3);
    return [date.getUTCFullYear(), pad(date.getUTCMonth() + 1), pad(date.getUTCDate())].join("");
  }
  /**
   * Returns a Date object without the time zone information.
   */
  toDateObject() {
    return new Date(this.utc);
  }
};

// src/psapi-data.ts
function isEPGEntry(entry) {
  if (!entry) {
    return false;
  }
  return Object.prototype.hasOwnProperty.call(entry, "programId");
}
var PSAPIData = class {
  loader;
  scheduleResponse;
  /**
   * The media URI for which data is loaded.
   */
  mediaURI;
  /**
   * The last fetched metadata.
   */
  metadata;
  /**
   * The last fetched manifest data.
   */
  manifest;
  /**
   * The last fetched (normalized) EPG data.
   */
  epg = [];
  constructor(loader, id) {
    this.loader = loader;
    const mediaURI = MediaURI.parse(id);
    if (!mediaURI) {
      throw new Error(`Failed to parse ${id}`);
    }
    this.mediaURI = mediaURI;
  }
  static isSupported(id) {
    if (!MediaURI.isValidFormat(id)) {
      return false;
    }
    const uri = MediaURI.parse(id);
    return uri !== void 0 && uri.scheme === "nrk" && (uri.type === void 0 || ["channel", "clip", "podcast", "program", "radioclip"].includes(uri.type));
  }
  /**
   * The last fetched schedule data, with playableInSeconds updated to reflect
   * current time (relying on the accuracy of the client clock).
   */
  get schedule() {
    if (this.scheduleResponse) {
      return {
        ...this.scheduleResponse,
        playableInSeconds: Math.max(0, (new Date(this.scheduleResponse.playableAt).getTime() - Date.now()) / 1e3)
      };
    }
    return void 0;
  }
  /**
   * Load metadata and replace any cached metadata.
   *
   * If the media URI doesn't have a type, then the type is extracted from the
   * metadata and added to the media URI.
   */
  async updateMetadata() {
    const response = await this.loader.fetchMetadata(this.mediaURI);
    this.metadata = response.data;
    if (!this.mediaURI.type && this.metadata) {
      this.mediaURI.type = getMediaURIType(this.metadata);
    }
    return response;
  }
  /**
   * Load the schedule API and replace cached data.
   */
  async updateSchedule() {
    if (!this.metadata) {
      throw new Error("Must load metadata first.");
    }
    const response = await this.loader.fetchSchedule(this.metadata);
    this.scheduleResponse = response.data;
    return { ...response, data: this.schedule };
  }
  /**
   * Load manifest, given the media is playable, and replace any cached
   * manifest data.
   */
  async updateManifest(type) {
    if (!this.metadata) {
      throw new Error("Must load metadata first.");
    }
    const response = await this.loader.fetchManifest(this.metadata, type);
    this.manifest = response.data;
    return response;
  }
  /**
   * Load EPG, parse some of the time values and replace any cached data.
   */
  async updateEPG() {
    if (!this.metadata) {
      throw new Error("Must load metadata first.");
    }
    const response = await this.loader.fetchEPG(this.metadata, this.mediaURI);
    this.epg = response.data.map((entry) => {
      const plannedStart = TimeWithTZ.parse(entry.plannedStart);
      const actualStart = TimeWithTZ.parse(entry.actualStart);
      const duration = toSeconds(parseDuration(entry.duration));
      const actualEnd = new TimeWithTZ(actualStart.utc + duration * 1e3, actualStart.tzOffset);
      const { programId, title, partNumber: part, relativeTimeType } = entry;
      const rating = entry.legalAge?.displayAge;
      return {
        programId,
        part,
        title,
        rating,
        plannedStart,
        actualStart,
        actualEnd,
        isCurrentlyLive: relativeTimeType === 2,
        webImages: entry.image.webImages.map((image) => ({ uri: image.imageUrl, width: image.pixelWidth }))
      };
    });
    const lastIndex = this.epg.length - 1;
    for (const [index, item] of this.epg.entries()) {
      if (index !== lastIndex) {
        item.actualEnd.utc = Math.min(item.actualEnd.utc, this.epg[index + 1].actualStart.utc);
      }
    }
    return { ...response, data: this.epg };
  }
  /**
   * Aborts all ongoing update requests. New requests can be made afterwards.
   */
  abort() {
    this.loader.abort();
  }
};
function getMediaURIType(metadata) {
  const type = metadata._links.self.href.replace(/^\/playback\/metadata\/|\/.*/g, "");
  if (["channel", "clip", "program", "podcast"].includes(type)) {
    return type;
  }
  return void 0;
}

// src/psapi-loader.ts
import { toError } from "@nrk/player-core/utils.js";
import ky, { HTTPError, TimeoutError } from "ky";

// src/manifest-link.ts
function getManifestLinks(metadata) {
  const manifestLinks = metadata._links.manifests;
  const manifestSubtitleLinks = metadata._links.manifestSubtitles || [];
  const manifestEmbeds = metadata._embedded.manifests;
  const manifestSubtitlesEmbeds = metadata._embedded.manifestSubtitles || [];
  if (!manifestLinks || !manifestEmbeds || !manifestSubtitleLinks || !manifestSubtitlesEmbeds) {
    return [];
  }
  const allManifestLinks = [...manifestLinks, ...manifestSubtitleLinks];
  const allManifests = [...manifestEmbeds, ...manifestSubtitlesEmbeds];
  return allManifests.map((embed) => {
    const name = embed._links.self.name;
    return {
      id: embed.id,
      // The self link in the embed points to the metadata, not the manifest.
      // Grab the manifest URL from the manifest links instead.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      href: allManifestLinks.find((link) => link.name === name).href,
      type: name,
      availabilityLabel: embed.availabilityLabel
    };
  });
}

// src/psapi-loader.ts
var DEFAULT_PLAYBACK_ORIGIN = "https://psapi.nrk.no";
var DEFAULT_RECOMMENDATIONS_ORIGIN = "https://anbefaling-psapi.nrk.no";
var DEFAULT_PLAYER_FAMILY = "player-core";
var PSAPILoader = class {
  authTokenFunction;
  enableEeaPortability;
  contentGroup;
  ageRestriction;
  playbackOrigin;
  recommendationsOrigin;
  playerName;
  playerFamily;
  abortController;
  globalParams;
  cdnOverride;
  preferredBandwidth;
  constructor(playerName, options) {
    const playbackOptions = options?.playback;
    const recommendationsOptions = options?.recommendations;
    this.authTokenFunction = playbackOptions && playbackOptions.authTokenFunction ? playbackOptions.authTokenFunction : () => Promise.resolve("");
    this.ageRestriction = playbackOptions && playbackOptions.ageRestriction;
    this.contentGroup = playbackOptions && playbackOptions.contentGroup;
    this.enableEeaPortability = playbackOptions?.enableEeaPortability;
    this.playbackOrigin = playbackOptions?.origin ?? DEFAULT_PLAYBACK_ORIGIN;
    this.recommendationsOrigin = recommendationsOptions?.origin ?? DEFAULT_RECOMMENDATIONS_ORIGIN;
    this.playerName = playerName;
    this.playerFamily = playbackOptions?.playerFamily ?? DEFAULT_PLAYER_FAMILY;
    this.abortController = new AbortController();
    this.globalParams = playbackOptions?.queryParams;
    this.cdnOverride = playbackOptions?.cdnOverride;
    this.preferredBandwidth = playbackOptions?.preferredBandwidth;
  }
  /**
   * Fetch from playback/metadata.
   */
  async fetchMetadata(mediaURI) {
    const path = `/playback/metadata${mediaURI.asPath()}`;
    return await this.loadFromPSAPI(path, this.buildQueryParams());
  }
  /**
   * Fetch from playback/schedule.
   */
  async fetchSchedule(metadata) {
    const path = metadata._links.schedule?.href;
    const queryParams = {};
    if (!path) {
      throw new Error("Schedule is not available.");
    }
    return await this.loadFromPSAPI(path, queryParams);
  }
  /**
   * Fetch from playback/manifest.
   */
  async fetchManifest(metadata, type = "default") {
    if (!metadata.playable) {
      throw new Error("The metadata must contain playable information before loading the manifest.");
    }
    const manifestLinks = getManifestLinks(metadata);
    const manifestLink = manifestLinks.find((m) => m.type === type);
    if (!manifestLink || !manifestLink.href) {
      throw new Error(`Unable to load unknown manifest type ${type}.`);
    }
    const headers = {};
    if (this.cdnOverride) {
      headers["cdnOverride"] = this.cdnOverride;
    }
    return await this.loadFromPSAPI(manifestLink.href, this.buildQueryParams(["preferredBandwidth"]), headers);
  }
  /**
   * Fetch from channels/.../epglivebuffer.
   */
  async fetchEPG(metadata, mediaURI) {
    if (mediaURI.type !== "channel") {
      throw new Error(`Unsupported media type for the EPG API. Got '${mediaURI.type}'`);
    }
    const queryParams = {};
    const path = metadata._links.epgBuffer?.href ?? `/channels/${mediaURI.id}/epglivebuffer`;
    return await this.loadFromPSAPI(path, queryParams, {}, async (response) => {
      assertStatusCode(response, [200, 204]);
      return response.status === 204 ? [] : await responseJSON(response);
    });
  }
  async fetchRecommendationsForProgram(prfId) {
    const path = `/programs/${prfId}/recommend-next`;
    return await this.loadFromPSAPIRecommendations(path, async (response) => {
      assertStatusCode(response, [200, 204]);
      return response.status === 204 ? null : await responseJSON(response);
    });
  }
  /**
   * Build common query parameters for playback requests
   */
  buildQueryParams(optionalsToInclude = []) {
    const queryParams = {};
    if (this.enableEeaPortability) {
      queryParams["eea-portability"] = "true";
    }
    if (this.contentGroup !== void 0) {
      queryParams["contentGroup"] = this.contentGroup;
    }
    if (this.ageRestriction !== void 0) {
      queryParams["ageRestriction"] = this.ageRestriction;
    }
    if (this.preferredBandwidth !== void 0 && optionalsToInclude.includes("preferredBandwidth")) {
      queryParams["preferredBandwidth"] = this.preferredBandwidth;
    }
    return queryParams;
  }
  /**
   * Assembles the Accept header needed for psapi/playback requests.
   */
  getPlaybackAcceptHeader() {
    return `application/vnd.nrk.psapi+json; version=9; player=${this.playerName}; device=${this.playerFamily}`;
  }
  /**
   * Loads a URL and returns the response data and some header information.
   */
  async loadFromPSAPI(path, queryParams, additionalHeaders, responseHandler) {
    const absoluteURL = new URL(path, this.playbackOrigin);
    const searchParamsFromUrl = [...absoluteURL.searchParams.entries()].reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value
      }),
      {}
    );
    const combinedParams = { ...this.globalParams, ...searchParamsFromUrl, ...queryParams };
    let response;
    const headers = {
      accept: this.getPlaybackAcceptHeader(),
      ...additionalHeaders
    };
    const token = await this.authTokenFunction();
    if (queryParams && queryParams["eea-portability"] && token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    try {
      response = await ky.get(absoluteURL.href, {
        signal: this.abortController.signal,
        headers,
        searchParams: Object.keys(combinedParams).length === 0 ? void 0 : combinedParams,
        mode: "cors",
        throwHttpErrors: !responseHandler
      });
    } catch (err) {
      if (toError(err).name === "AbortError") {
        throw err;
      }
      if (err instanceof HTTPError) {
        throwStatusCodeError(err.response);
      }
      const errorType = err instanceof TimeoutError ? "TIMEOUT_ERROR" /* TIMEOUT_ERROR */ : "NETWORK_ERROR" /* NETWORK_ERROR */;
      throw new PSAPIRequestError(`${err}`, errorType);
    }
    const data = responseHandler ? await responseHandler(response) : await this.defaultResponseHandler(response);
    return {
      maxAge: extractMaxAge(response),
      data
    };
  }
  async loadFromPSAPIRecommendations(path, responseHandler) {
    const absoluteURL = new URL(path, this.recommendationsOrigin);
    const token = await this.authTokenFunction();
    const headers = {
      accept: "application/json"
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    let response;
    try {
      response = await ky.get(absoluteURL.href, {
        signal: this.abortController.signal,
        headers,
        mode: "cors",
        throwHttpErrors: !responseHandler
      });
    } catch (err) {
      if (toError(err).name === "AbortError") {
        throw err;
      }
      if (err instanceof HTTPError) {
        throwStatusCodeError(err.response);
      }
      const errorType = err instanceof TimeoutError ? "TIMEOUT_ERROR" /* TIMEOUT_ERROR */ : "NETWORK_ERROR" /* NETWORK_ERROR */;
      throw new PSAPIRequestError(`${err}`, errorType);
    }
    const data = responseHandler ? await responseHandler(response) : await this.defaultResponseHandler(response);
    return {
      maxAge: extractMaxAge(response),
      data
    };
  }
  /**
   * The default response handler for load requests. It expects a 200 response
   * and returns the JSON data from the response body.
   */
  async defaultResponseHandler(response) {
    assertStatusCode(response, 200);
    return await responseJSON(response);
  }
  /**
   * Aborts all ongoing fetch requests. New fetches can be made afterwards.
   */
  abort() {
    this.abortController.abort();
    this.abortController = new AbortController();
  }
};
function assertStatusCode(response, status) {
  if (Array.isArray(status) ? !status.includes(response.status) : response.status !== status) {
    throwStatusCodeError(response);
  }
}
function throwStatusCodeError(response) {
  if (response.status === 404 || response.status === 410) {
    throw new PSAPIRequestError(`${response.url} doesn't exist.`, "NOT_FOUND_ERROR" /* NOT_FOUND_ERROR */);
  }
  throw new PSAPIRequestError(
    `Bad response: ${response.status} ${response.statusText}`,
    "BAD_RESPONSE_ERROR" /* BAD_RESPONSE_ERROR */
  );
}
function extractMaxAge(response) {
  const cacheControl = response.headers.get("Cache-Control");
  let m;
  if (cacheControl && (m = cacheControl.match(/max-age=(\d+)/))) {
    return Number.parseInt(m[1], 10);
  }
  return void 0;
}
async function responseJSON(response) {
  try {
    return await response.json();
  } catch (err) {
    const error = toError(err);
    if (error.name === "TypeError" || error.name === "SyntaxError") {
      throw new PSAPIRequestError(`Response content problem: ${error}`, "BAD_RESPONSE_ERROR" /* BAD_RESPONSE_ERROR */);
    }
    throw new PSAPIRequestError(`${error}`, "NETWORK_ERROR" /* NETWORK_ERROR */);
  }
}

// src/psapi-client.ts
var logger = getLogger("PSAPIClient");
var EPG_RELOAD_TIME = 15 * 60 * 1e3;
var MIN_METADATA_RELOAD_TIME = 5e3;
var MAX_RANDOM_EXTRA_WAIT_TIME = 5e3;
var PSAPIClient = class extends PSAPIData {
  abortController;
  psapiOptions;
  constructor(id, playerName, options) {
    const loader = new PSAPILoader(playerName, options);
    super(loader, id);
    this.abortController = new AbortController();
    this.psapiOptions = options;
  }
  /** Returns a copy of the PSAPIOptions */
  getPSAPIOptions() {
    return { ...this.psapiOptions };
  }
  static isSupported(id) {
    return PSAPIData.isSupported(id);
  }
  /**
   * Returns an iterator which ends when the metadata is/becomes playable.
   *
   * The iterator can end (default) or throw an error if the metadata never
   * becomes playable, depending on the options argument.
   *
   * It uses the schedule API if available, updating the metadata with more
   * accurate information about when the media becomes playable.
   */
  loadMetadata(options) {
    const self = this;
    const abortController = this.abortController;
    const throwOnNonPlayable = options?.throwNonPlayableError ?? false;
    let responseCacheTime;
    let done = false;
    return {
      async next() {
        if (abortController.signal.aborted) {
          throw new AbortError("Already aborted.");
        }
        if (done) {
          return Promise.resolve({
            done: true,
            value: void 0
          });
        }
        if (self.metadata) {
          const playableAt = self.schedule?.playableAt ? new Date(self.schedule.playableAt) : getAvailableFrom(self.metadata);
          const timeUntilPlayable = (playableAt?.getTime() ?? 0) - Date.now();
          const waitTime = Math.max(
            responseCacheTime ? responseCacheTime * 1e3 : 0,
            options?.minReloadTime ?? MIN_METADATA_RELOAD_TIME,
            timeUntilPlayable / 2
            // Update at half-time.
          ) + Math.random() * MAX_RANDOM_EXTRA_WAIT_TIME;
          logger.log(`Media playable at ${playableAt}. Reloading in ${waitTime / 1e3} seconds.`);
          await wait(waitTime, abortController.signal);
        }
        responseCacheTime = await self.updateMetadataOrSchedule();
        const mergedMetadata = mergeMetadataAndSchedule(self.metadata, self.schedule);
        const availableTo = getAvailableTo(mergedMetadata);
        done = mergedMetadata.playability === "playable" || mergedMetadata.nonPlayable?.messageType && mergedMetadata.nonPlayable?.messageType !== "ProgramRightsHasNotStarted" || !availableTo || availableTo < /* @__PURE__ */ new Date();
        if (done && throwOnNonPlayable && mergedMetadata.playability !== "playable") {
          const nonPlayableErrorType = getNonPlayableErrorType(mergedMetadata);
          throw new NonPlayableError(
            "Media is non-playable",
            nonPlayableErrorType,
            mergedMetadata.nonPlayable.endUserMessage,
            mergedMetadata.nonPlayable.endUserMessageSupplement
          );
        }
        return {
          done: false,
          value: mergedMetadata
        };
      },
      [Symbol.asyncIterator]() {
        return this;
      }
    };
  }
  /**
   * Update the metadata or schedule or both. Returns the cache time of the API
   * which is most likely to be used first next time, if available.
   */
  async updateMetadataOrSchedule() {
    let metadataUpdated = false;
    let scheduleUpdated = false;
    let metadataCacheTime;
    let scheduleCacheTime;
    if (!this.metadata?._links.schedule) {
      const response = await this.updateMetadata();
      metadataCacheTime = response.maxAge;
      metadataUpdated = true;
    }
    if (this.metadata?._links.schedule) {
      try {
        const response = await this.updateSchedule();
        scheduleCacheTime = response.maxAge;
        scheduleUpdated = true;
      } catch (err) {
        logger.error("Failed to update schedule:", err);
        const error = toError2(err);
        if (error.name === "PSAPIRequestError" && error.type === "NOT_FOUND_ERROR" /* NOT_FOUND_ERROR */) {
          this.scheduleResponse = void 0;
        }
      }
    }
    if (!metadataUpdated && (!scheduleUpdated || this.schedule?.playableNow)) {
      const response = await this.updateMetadata();
      metadataCacheTime = response.maxAge;
    }
    return scheduleCacheTime ?? metadataCacheTime;
  }
  /**
   * Returns an iterator which (re-)loads the EPG at certain intervals.
   */
  loadEPG(reloadInterval = EPG_RELOAD_TIME) {
    let first = true;
    const self = this;
    const abortController = this.abortController;
    return {
      async next() {
        if (abortController.signal.aborted) {
          throw new AbortError("Already aborted.");
        }
        if (first) {
          first = false;
        } else {
          const waitTime = reloadInterval + Math.round(Math.random() * 60 * 1e3);
          await wait(waitTime, abortController.signal);
        }
        try {
          await self.updateEPG();
        } catch (err) {
          if (toError2(err).name === "AbortError") {
            throw err;
          }
          logger.log(`Failed to update EPG: ${err}`);
        }
        return {
          done: false,
          value: self.epg
        };
      },
      [Symbol.asyncIterator]() {
        return this;
      }
    };
  }
  loadRecommendationsForProgram() {
    const id = this.mediaURI.id;
    if (!id) {
      throw new Error("mediaURI.id is undefined");
    }
    return this.loader.fetchRecommendationsForProgram(id);
  }
  /**
   * Aborts all ongoing requests and active iterators with AbortError. New
   * requests and iterators can be started afterwards.
   */
  abort() {
    this.abortController.abort();
    this.abortController = new AbortController();
    super.abort();
  }
};
async function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    const abort = () => {
      clearTimeout(timeout);
      signal.removeEventListener("abort", abort);
      reject(new AbortError("PSAPI client waiting aborted."));
    };
    if (signal.aborted) {
      abort();
    } else {
      signal.addEventListener("abort", abort);
    }
  });
}
function getAvailableFrom(metadata) {
  const availableFrom = metadata?.availability.onDemand?.from ?? metadata?.availability.live?.transmissionInterval?.from;
  return availableFrom ? new Date(availableFrom) : void 0;
}
function getAvailableTo(metadata) {
  const availableTo = metadata?.availability.onDemand?.to ?? metadata?.availability.live?.transmissionInterval?.to;
  return availableTo ? new Date(availableTo) : void 0;
}
function mergeMetadataAndSchedule(metadata, schedule) {
  if (!schedule || schedule.playableNow && metadata.playability === "nonPlayable") {
    return metadata;
  }
  let alteredOnDemand = null;
  let alteredLive = null;
  if (metadata.availability.onDemand) {
    alteredOnDemand = {
      ...metadata.availability.onDemand,
      from: schedule.playableAt,
      hasRightsNow: false
    };
  } else if (metadata.availability.live) {
    alteredLive = {
      ...metadata.availability.live,
      isOngoing: false,
      transmissionInterval: {
        ...metadata.availability.live.transmissionInterval,
        from: schedule.playableAt
      }
    };
  }
  let alteredMetadata = {
    ...metadata,
    availability: {
      ...metadata.availability,
      onDemand: alteredOnDemand,
      live: alteredLive
    }
  };
  if (schedule.playableNow === false && metadata.playability === "playable") {
    alteredMetadata = {
      ...alteredMetadata,
      _links: {
        ...alteredMetadata._links,
        manifests: null
      },
      playability: "nonPlayable",
      playable: null,
      nonPlayable: {
        reason: "blocked",
        messageType: "ProgramRightsHasNotStarted",
        endUserMessage: schedule.endUserMessage,
        endUserMessageSupplement: "",
        helpUrl: "https://info.nrk.no/tv/",
        availableAbroadUrl: null,
        userAction: null
      }
    };
  }
  alteredMetadata.nonPlayable = {
    ...alteredMetadata.nonPlayable,
    endUserMessage: schedule.endUserMessage
  };
  return alteredMetadata;
}
function getNonPlayableErrorType(metadata) {
  if (!metadata.nonPlayable) {
    return "UNPLAYABLE_ERROR" /* UNPLAYABLE_ERROR */;
  }
  const map = {
    ChannelAvailableWithNorwegianResidentClaim: "CHANNEL_AVAILABLE_NORWEGIAN_RESIDENT_CLAIM_ERROR" /* CHANNEL_AVAILABLE_NORWEGIAN_RESIDENT_CLAIM_ERROR */,
    ChannelIsGeoblocked: "CHANNEL_GEOBLOCKED_ERROR" /* CHANNEL_GEOBLOCKED_ERROR */,
    ChannelUnavailable: "CHANNEL_UNAVAILABLE_ERROR" /* CHANNEL_UNAVAILABLE_ERROR */,
    ChannelExceedsProfileRestrictions: "CHANNEL_EXCEEDS_PROFILE_RESTRICTIONS" /* CHANNEL_EXCEEDS_PROFILE_RESTRICTIONS */,
    ProgramAvailableWithNorwegianResidentClaim: "PROGRAM_AVAILABLE_NORWEGIAN_RESIDENT_CLAIM_ERROR" /* PROGRAM_AVAILABLE_NORWEGIAN_RESIDENT_CLAIM_ERROR */,
    ProgramIsGeoblocked: "PROGRAM_GEOBLOCKED_ERROR" /* PROGRAM_GEOBLOCKED_ERROR */,
    ProgramIsInLiveBuffer: "PROGRAM_IN_LIVE_BUFFER_ERROR" /* PROGRAM_IN_LIVE_BUFFER_ERROR */,
    ProgramIsNotTranscoded: "PROGRAM_NOT_TRANSCODED_ERROR" /* PROGRAM_NOT_TRANSCODED_ERROR */,
    ProgramRightsHasExpired: "PROGRAM_RIGHTS_EXPIRED_ERROR" /* PROGRAM_RIGHTS_EXPIRED_ERROR */,
    ProgramRightsHasNotStarted: "PROGRAM_RIGHTS_NOT_STARTED_ERROR" /* PROGRAM_RIGHTS_NOT_STARTED_ERROR */,
    ProgramExceedsProfileRestrictions: "PROGRAM_EXCEEDS_PROFILE_RESTRICTIONS" /* PROGRAM_EXCEEDS_PROFILE_RESTRICTIONS */,
    ProgramUnavailable: "PROGRAM_UNAVAILABLE_ERROR" /* PROGRAM_UNAVAILABLE_ERROR */,
    NoMessage: "PROGRAM_UNAVAILABLE_ERROR" /* PROGRAM_UNAVAILABLE_ERROR */
  };
  return map[metadata.nonPlayable.messageType] ?? "PROGRAM_UNAVAILABLE_ERROR" /* PROGRAM_UNAVAILABLE_ERROR */;
}
export {
  AbortError,
  MediaURI,
  NonPlayableError,
  NonPlayableErrorType,
  PSAPIClient,
  PSAPIData,
  PSAPILoader,
  PSAPIRequestError,
  PSAPIRequestErrorType,
  TimeWithTZ,
  getManifestLinks,
  getNonPlayableErrorType,
  isEPGEntry
};
